import { useCallback, useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { useAuthState } from "@/v2/hooks/useAuthState";
import { SearchResultsPanel } from "./SearchResultsPanel";
import { useDebouncedScheduleSearch } from "./useDebouncedScheduleSearch";

type HeaderSearchProps = {
  customer: string;
};

const LIMIT = 5;

export function MobileHeaderSearch({ customer }: HeaderSearchProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    filters,
    setSearchText,
    resetSearch,
    toggleFilter,
    selectedTeams,
    selectedChannels,
    liveQuery,
    vodQuery,
    channelsQuery,
    favoritesQuery,
  } = useDebouncedScheduleSearch({ customer, isAuthenticated, enabled: isOpen, limit: LIMIT });

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleResetSearch = useCallback(() => {
    resetSearch();
    inputRef.current?.focus();
  }, [resetSearch]);

  // Autofocus the input once the overlay is actually open, rather than a
  // raw `autoFocus` prop which would fire as soon as this (always-mounted)
  // input mounts, before the overlay is visible.
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDropdown();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeDropdown]);

  const handleSeeAll = useCallback(
    (route: string) => {
      const params = createSearchParams({
        search: filters.search || "",
        team: filters.team || "",
        channel_id: filters.channel || "",
      });
      navigate({ pathname: route, search: `?${params.toString()}` });
      closeDropdown();
    },
    [filters, navigate, closeDropdown]
  );

  const handleManageFavorites = useCallback(() => {
    navigate(PageRoutes.AccountSettings);
    closeDropdown();
  }, [navigate, closeDropdown]);

  return (
    <div ref={containerRef} className="min-[1200px]:hidden">
      {/* Mobile search trigger button in the header */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="Search"
      >
        {isOpen ? <X className="size-5" /> : <Search className="size-5" />}
      </button>

      {/* Mobile search overlay drawer */}
      {isOpen && (
        <div className="v2 fixed inset-x-2 top-[68px] z-[100] max-h-[80vh] overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-popover shadow-lg">
          <div className="flex flex-col">
            {/* Mobile-only internal search input */}
            <div className="sticky top-0 z-10 flex items-center gap-2 bg-muted p-3">
              <div className="relative flex h-9 w-full items-center rounded-md border border-input bg-background px-3 shadow-xs">
                <Search className="mr-2 size-4 shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  aria-label="Search games and channels"
                  value={filters.search}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="v2-search-input w-full border-none bg-transparent p-0 text-sm outline-none placeholder:text-muted-foreground"
                />
                {filters.search ? (
                  <button
                    type="button"
                    onClick={handleResetSearch}
                    className="absolute right-2 flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
            </div>

            <SearchResultsPanel
              idPrefix="mobile"
              layout="mobile"
              filters={filters}
              selectedTeams={selectedTeams}
              selectedChannels={selectedChannels}
              toggleFilter={toggleFilter}
              liveQuery={liveQuery}
              vodQuery={vodQuery}
              channelsQuery={channelsQuery}
              favoritesQuery={favoritesQuery}
              isAuthenticated={isAuthenticated}
              limit={LIMIT}
              onSeeAll={handleSeeAll}
              onManageFavorites={handleManageFavorites}
            />
          </div>
        </div>
      )}
    </div>
  );
}
